import { useTranslation } from "@msuite/ui/localization";
import { Tooltip } from "@msuite/ui/tooltip";
import { INCOMING_INVOICE_CHECKS } from "@msuite/utils/incoming-invoices";
import type { IncomingInvoiceCheckList } from "@msuite/utils/types";
import { IncomingInvoiceCheckBadge } from "./incoming-invoice-check-badge";
import { INCOMING_INVOICE_CHECK_LABEL } from "./incoming-invoice-check-labels";
import { IncomingInvoiceCheckHint } from "./incoming-invoice-check-hint";

/** Props Interface */
interface IncomingInvoiceCheckBadgesProps {
	checks: IncomingInvoiceCheckList;
	currency: string;
}

/**
 * Every review check of an invoice as a mark, in the order `INCOMING_INVOICE_CHECKS` lists
 * them, so a column of rows reads at a glance where something failed. Each mark names its check,
 * and the hint behind it, on hover.
 */
export function IncomingInvoiceCheckBadges({ checks, currency }: IncomingInvoiceCheckBadgesProps) {
	/** Context */
	const { t } = useTranslation();

	/** Render */
	// Pinned inside the cell like the processing-state tag, so the marks never set the row's height
	// and sit centred on it.
	return (
		<div className="absolute inset-y-0 inset-x-2 h-stack items-center gap-1">
			{INCOMING_INVOICE_CHECKS.map(({ key }) => {
				const check = checks.find((candidate) => candidate.checkKey === key) ?? null;
				return (
					<Tooltip
						key={key}
						label={
							<span className="h-stack items-center gap-2">
								<span>{t(INCOMING_INVOICE_CHECK_LABEL[key])}</span>
								{check && (
									<IncomingInvoiceCheckHint
										check={check}
										currency={currency}
									/>
								)}
							</span>
						}
					>
						<span
							className="flex-center cursor-pointer"
							data-cy={`incoming-invoice-check-badge-${key}`}
						>
							<IncomingInvoiceCheckBadge state={check?.state ?? "unchecked"} />
						</span>
					</Tooltip>
				);
			})}
		</div>
	);
}
